import { useContext, useEffect, useRef } from 'react';
import { Badge, Box, CloseButton, Flex, Text } from '@chakra-ui/react';
import SectionWrapper from '../../pass-templates-new/SectionWrapper';
import CustomSearchableSelect from '../../../common/CustomSearchableSelect';

import {
  useListGroupTagsQuery,
  useLazyListGroupTagsQuery,
} from '../../../../store/redux/api/passApi';
import { MeContext } from '../../../../context/MeContext';
import type { MeContextState } from '../../../../store/types/account';

interface GroupTagsSectionProps {
  tags: string[];
  isEdit?: boolean;
  entityId?: string;
  focusedField?: string | null;
  onTagsChange?: (tags: string[]) => void;
  onFieldFocus?: (identifier: string) => void;
}

const GroupTagsSection = ({
  tags,
  isEdit,
  entityId: entityIdProp,
  focusedField,
  onTagsChange,
  onFieldFocus,
}: GroupTagsSectionProps) => {
  const meCtx = useContext(MeContext) as { state: MeContextState };
  const { filterEntity } = meCtx.state;
  const resolvedEntityId = entityIdProp || filterEntity?.uuid;

  const { currentData } = useListGroupTagsQuery(
    { entityId: resolvedEntityId },
    { skip: !isEdit, refetchOnMountOrArgChange: true }
  );
  const [triggerSearchGroupTags] = useLazyListGroupTagsQuery();

  const groupTagOptions = (currentData?.data || []).map((tag) => ({
    label: tag.id,
    value: tag.id,
    count: tag.attributes?.count,
  }));

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isEdit || !tags.length) return;
    onTagsChange?.([]);
  }, [resolvedEntityId]);

  const removeTag = (tag: string) => {
    onTagsChange?.(tags.filter((t) => t !== tag));
  };

  return (
    <SectionWrapper
      title="Group tags"
      openOnLoad
      subtitle={
        <Text fontSize="15px" color="grey">
            Organise this pass into groups for filtering, bulk updates and
            messaging
        </Text>
      }
      tooltip={
        isEdit
          ? 'Group tags let you organise passes for filtering, bulk updates and messaging'
          : 'Segment passes into groups by location, tier or campaign, then filter, bulk update or message them all at once'
      }
      id="group-tags-section"
    >
      {isEdit ? (
        <Box display="flex" flexDirection="column" gap={4}>
          {tags.length > 0 && (
            <Flex flexWrap="wrap" gap={3}>
              {tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="groupTag"
                  display="flex"
                  alignItems="center"
                  gap="7px"
                >
                  {tag}
                  <CloseButton
                    size="sm"
                    onClick={() => removeTag(tag)}
                    sx={{
                      minW: 'auto',
                      h: 'auto',
                      w: 'auto',
                      '& svg': { w: '8px', h: '8px' },
                    }}
                  />
                </Badge>
              ))}
            </Flex>
          )}

            <Box>
              <Text textStyle="bodyFamilyRegular" fontSize="16px" color="black">
                Add group tags
              </Text>
              <CustomSearchableSelect
                key={resolvedEntityId || 'all'}
                options={groupTagOptions}
                placeholder="Type to add or search tag..."
                onFocus={() => onFieldFocus?.('groupTags')}
                onChange={(selected) => {
                  if (selected) {
                    const newTags = selected.map(
                      (s: { value: string }) => s.value
                    );
                    onTagsChange?.(newTags);
                  } else {
                    onTagsChange?.([]);
                  }
                }}
                loadOptions={async (searchTerm: string) => {
                  try {
                    const result = await triggerSearchGroupTags({
                      entityId: resolvedEntityId,
                      tag: searchTerm,
                    }).unwrap();
                    return (result?.data || []).map((tag) => ({
                      label: tag.id,
                      value: tag.id,
                      count: tag.attributes?.count,
                    }));
                  } catch {
                    return [];
                  }
                }}
                value={tags.map((tag) => ({ label: tag, value: tag }))}
                isMulti
                passBuilder
              />
              <Text fontSize="12px" color="#4C4C4C" mt="8px">
                Use lowercase letters, numbers and hyphens only
              </Text>
            </Box>
        </Box>
      ) : (
        <Box>
          <Flex flexWrap="wrap" gap={3}>
            {tags.map((tag) => (
              <Badge key={tag} variant="groupTag">
                {tag}
              </Badge>
            ))}
          </Flex>
        </Box>
      )}
    </SectionWrapper>
  );
};

export default GroupTagsSection;
